import {
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  UserOutlined
} from "@ant-design/icons";
import { App, Button, Form, Input } from "antd";
import { useEffect, useState } from "react";
import navigationLogo from "../assets/navigation-logo-ai-transparent-256.png";
import {
  getAuthConfig,
  login,
  resetPassword,
  sendEmailCode,
  setToken
} from "../services/api";
import type { SessionUser } from "../types";

type AuthMode = "login" | "forgot";
type LoginValues = { identifier: string; password: string; totp?: string };
type ResetValues = {
  email: string;
  code: string;
  password: string;
  confirmPassword: string;
};

function useCodeCooldown() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1_000);
    return () => window.clearInterval(timer);
  }, [seconds]);
  return { seconds, start: () => setSeconds(60) };
}

export function LoginPage({ onLogin }: { onLogin: (user: SessionUser) => void }) {
  const { message } = App.useApp();
  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [resetForm] = Form.useForm<ResetValues>();
  const resetCooldown = useCodeCooldown();

  useEffect(() => {
    getAuthConfig()
      .then((config) => {
        setEmailAvailable(config.emailCodeAvailable);
      })
      .catch(() => undefined);
  }, []);

  const finishSession = (session: { token: string; user: SessionUser }) => {
    setToken(session.token);
    onLogin(session.user);
  };

  const sendCode = async (
    form: ReturnType<typeof Form.useForm<ResetValues>>[0],
    start: () => void
  ) => {
    try {
      const { email } = await form.validateFields(["email"]);
      const result = await sendEmailCode(String(email), "PASSWORD_RESET");
      start();
      void message.success(result.message);
    } catch (error) {
      if (error instanceof Error) void message.error(error.message);
    }
  };

  const loginForm = (
    <Form<LoginValues>
      layout="vertical"
      size="large"
      onFinish={async (values) => {
        setLoading(true);
        try {
          finishSession(await login(values.identifier, values.password, values.totp));
        } catch (error) {
          void message.error(error instanceof Error ? error.message : "登录失败");
        } finally {
          setLoading(false);
        }
      }}
    >
      <Form.Item
        label="邮箱或操作员名称"
        name="identifier"
        rules={[{ required: true, message: "请输入邮箱或操作员名称" }]}
      >
        <Input prefix={<UserOutlined />} autoComplete="username" placeholder="pilot@example.com" />
      </Form.Item>
      <Form.Item label="密码" name="password" rules={[{ required: true, message: "请输入密码" }]}>
        <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
      </Form.Item>
      <Form.Item
        label="动态验证码（按账号设置）"
        name="totp"
        rules={[{ len: 6, message: "请输入 6 位动态码" }]}
        extra="未开启动态验证码的账号无需填写"
      >
        <Input
          prefix={<SafetyCertificateOutlined />}
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
        />
      </Form.Item>
      <div className="login-actions">
        <Button type="link" onClick={() => setMode("forgot")}>忘记密码？</Button>
      </div>
      <Button block type="primary" htmlType="submit" loading={loading} size="large">
        登录后台
      </Button>
    </Form>
  );

  const resetFormNode = (
    <Form<ResetValues>
      form={resetForm}
      layout="vertical"
      size="large"
      scrollToFirstError={{ focus: true }}
      onFinish={async (values) => {
        setLoading(true);
        try {
          await resetPassword(values);
          void message.success("密码已重置，请使用新密码登录");
          resetForm.resetFields();
          setMode("login");
        } catch (error) {
          void message.error(error instanceof Error ? error.message : "密码重置失败");
        } finally {
          setLoading(false);
        }
      }}
    >
      <Form.Item label="注册邮箱" name="email" rules={[{ required: true }, { type: "email" }]}>
        <Input prefix={<MailOutlined />} autoComplete="email" />
      </Form.Item>
      <Form.Item label="邮箱验证码" required>
        <div className="verification-row">
          <Form.Item name="code" noStyle rules={[{ required: true }, { len: 6 }]}>
            <Input prefix={<KeyOutlined />} inputMode="numeric" maxLength={6} />
          </Form.Item>
          <Button
            disabled={!emailAvailable || resetCooldown.seconds > 0}
            onClick={() => void sendCode(resetForm, resetCooldown.start)}
          >
            {resetCooldown.seconds ? `${resetCooldown.seconds}s` : "发送验证码"}
          </Button>
        </div>
      </Form.Item>
      <Form.Item label="新密码" name="password" rules={[{ required: true }, { min: 12 }]}>
        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        label="确认新密码"
        name="confirmPassword"
        dependencies={["password"]}
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator(_, value) {
              return !value || getFieldValue("password") === value
                ? Promise.resolve()
                : Promise.reject(new Error("两次输入的密码不一致"));
            }
          })
        ]}
      >
        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
      </Form.Item>
      <Button block type="primary" htmlType="submit" loading={loading}>
        重置密码
      </Button>
      <Button block type="link" onClick={() => setMode("login")}>返回登录</Button>
    </Form>
  );

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="login-brand">
          <img src={navigationLogo} alt="领航计划" />
          <div>
            <h1>领航计划</h1>
            <span>邀请与玩家来源管理后台</span>
          </div>
        </div>
        <p className="login-card__intro">
          使用后台管理员账号进入真实领航账本，管理玩家来源、风险、信誉与新人权益。
        </p>
        {mode === "forgot" ? resetFormNode : loginForm}
        <footer>
          <span>账号由 Owner 在后台统一添加；动态验证码可由每个账号自行开启</span>
        </footer>
      </section>
    </main>
  );
}
